import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  TrendingUp, TrendingDown, Wallet, DollarSign, AlertTriangle,
  Calendar, Users, PieChart, FolderTree
} from 'lucide-react';
import { formatCurrency } from '../utils/invoiceData';
import { buildCategoryLookup, getRootCategory } from './ExpenseCategories';
import { getAccountLabel } from '../utils/expenseData';
import {
  startOfMonth, endOfMonth, subMonths, startOfYear, addDays,
  isWithinInterval, format
} from 'date-fns';
import './Expenses.css';

const toDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val === 'string') {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val.toDate === 'function') return val.toDate();
  if (typeof val === 'number') return new Date(val);
  return null;
};

// Parse "HH:MM" into minutes-of-day. Returns null if invalid.
const parseClockMinutes = (s) => {
  if (typeof s !== 'string' || !s.includes(':')) return null;
  const [h, m] = s.split(':').map(n => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// Hours for a single shift based on startTime/endTime "HH:MM" strings.
const hoursForShift = (shift) => {
  const start = parseClockMinutes(shift.startTime);
  const end = parseClockMinutes(shift.endTime);
  if (start === null || end === null) return 0;
  const diff = (end - start) / 60;
  return diff > 0 ? diff : 0;
};

function CashflowReport() {
  const { state } = useApp();
  const [rangePreset, setRangePreset] = useState('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [upcomingHorizon, setUpcomingHorizon] = useState('30'); // days

  const dateBounds = useMemo(() => {
    const today = new Date();
    switch (rangePreset) {
      case 'this_month':
        return { start: startOfMonth(today), end: endOfMonth(today) };
      case 'last_month': {
        const lm = subMonths(today, 1);
        return { start: startOfMonth(lm), end: endOfMonth(lm) };
      }
      case 'last_3_months':
        return { start: startOfMonth(subMonths(today, 2)), end: endOfMonth(today) };
      case 'year_to_date':
        return { start: startOfYear(today), end: today };
      case 'custom':
        return {
          start: customStart ? new Date(customStart + 'T00:00:00') : today,
          end: customEnd ? new Date(customEnd + 'T23:59:59') : today
        };
      default:
        return { start: startOfMonth(today), end: endOfMonth(today) };
    }
  }, [rangePreset, customStart, customEnd]);

  const categories = useMemo(() => state.expenseCategories || [], [state.expenseCategories]);
  const categoryLookup = useMemo(() => buildCategoryLookup(categories), [categories]);

  // ===== Income in period =====
  const incomeInPeriod = useMemo(() => {
    return (state.deposits || []).filter(d => {
      const dt = toDate(d.date);
      return dt && isWithinInterval(dt, dateBounds);
    });
  }, [state.deposits, dateBounds]);

  const totalIncome = incomeInPeriod.reduce((s, d) => s + (Number(d.amount) || 0), 0);

  // ===== Expenses in period =====
  const expensesInPeriod = useMemo(() => {
    return (state.expenses || []).filter(e => {
      const dt = toDate(e.dueDate);
      return dt && isWithinInterval(dt, dateBounds);
    });
  }, [state.expenses, dateBounds]);

  const totalExpenses = expensesInPeriod.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  // ===== Expense breakdown by parent category (P&L rollup) =====
  const expenseBreakdown = useMemo(() => {
    // root label -> { total, children: { name -> total } }
    const rollup = {};
    const UNCATEGORIZED = '__uncategorized__';

    expensesInPeriod.forEach(exp => {
      const amount = Number(exp.amount) || 0;
      let rootLabel;
      let childLabel = null;

      if (exp.categoryId && categoryLookup[exp.categoryId]) {
        const cat = categoryLookup[exp.categoryId];
        const root = getRootCategory(exp.categoryId, categoryLookup);
        rootLabel = root?.name || cat.name;
        // Only show a distinct child row if this cat differs from the root
        if (root && root.id !== cat.id) childLabel = cat.name;
      } else if (exp.account) {
        // Legacy data: use the old hardcoded label as its own parent row
        rootLabel = getAccountLabel(exp.account);
      } else {
        rootLabel = 'Uncategorized';
      }

      const key = rootLabel || UNCATEGORIZED;
      if (!rollup[key]) rollup[key] = { total: 0, children: {} };
      rollup[key].total += amount;
      if (childLabel) {
        rollup[key].children[childLabel] = (rollup[key].children[childLabel] || 0) + amount;
      }
    });

    // Convert to sorted array
    return Object.entries(rollup)
      .map(([name, data]) => ({
        name,
        total: data.total,
        children: Object.entries(data.children)
          .map(([n, t]) => ({ name: n, total: t }))
          .sort((a, b) => b.total - a.total)
      }))
      .sort((a, b) => b.total - a.total);
  }, [expensesInPeriod, categoryLookup]);

  // ===== Upcoming sales (unpaid invoices within horizon) =====
  const upcomingSales = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const horizonEnd = addDays(today, parseInt(upcomingHorizon, 10) || 30);
    const UNPAID_STATUSES = new Set(['draft', 'sent', 'pending', 'overdue', 'partial']);

    return (state.invoices || [])
      .filter(inv => {
        if (!UNPAID_STATUSES.has(inv.status)) return false;
        const due = toDate(inv.dueDate);
        if (!due) return false;
        return due >= today && due <= horizonEnd;
      })
      .map(inv => {
        const balance = inv.balance != null ? Number(inv.balance) : Number(inv.total || 0) - Number(inv.amountPaid || 0);
        const client = (state.clients || []).find(c => c.id === inv.clientId);
        return {
          id: inv.id,
          invoiceNumber: inv.invoiceNumber || inv.id,
          clientName: client ? (client.companyName || `${client.firstName || ''} ${client.lastName || ''}`.trim()) : '—',
          dueDate: inv.dueDate,
          status: inv.status,
          balance: balance
        };
      })
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  }, [state.invoices, state.clients, upcomingHorizon]);

  const upcomingSalesTotal = upcomingSales.reduce((s, u) => s + u.balance, 0);

  // ===== Estimated payroll in period (from scheduled shifts × hourlyRate) =====
  const payrollByEmployee = useMemo(() => {
    const empById = {};
    (state.employees || []).forEach(emp => { empById[emp.id] = emp; });

    const buckets = {};
    (state.shifts || []).forEach(shift => {
      const dt = toDate(shift.date);
      if (!dt || !isWithinInterval(dt, dateBounds)) return;
      const hours = hoursForShift(shift);
      if (hours <= 0) return;
      const emp = empById[shift.employeeId];
      const name = emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : 'Unknown';
      const rate = Number(emp?.hourlyRate || 0);
      if (!buckets[shift.employeeId]) {
        buckets[shift.employeeId] = { name, hours: 0, rate, cost: 0 };
      }
      buckets[shift.employeeId].hours += hours;
      buckets[shift.employeeId].cost += hours * rate;
    });

    return Object.values(buckets).sort((a, b) => b.cost - a.cost);
  }, [state.shifts, state.employees, dateBounds]);

  const estimatedPayrollTotal = payrollByEmployee.reduce((s, p) => s + p.cost, 0);
  const estimatedPayrollHours = payrollByEmployee.reduce((s, p) => s + p.hours, 0);

  // ===== P&L summary =====
  const netIncome = totalIncome - totalExpenses;

  // Format the selected range
  const rangeLabel = `${format(dateBounds.start, 'MMM d, yyyy')} – ${format(dateBounds.end, 'MMM d, yyyy')}`;

  return (
    <div className="expenses-page">
      <div className="expenses-header">
        <div className="header-title">
          <h1>Cashflow & P&amp;L</h1>
          <div className="subtitle">Upcoming sales, estimated payroll, categorized expenses, and profit/loss for the selected period</div>
        </div>
        <div className="header-actions">
          <Link className="btn-secondary" to="/admin/expense-categories">
            <FolderTree size={16} /> Categories
          </Link>
          <Link className="btn-secondary" to="/admin/accounts">
            <Wallet size={16} /> Accounts
          </Link>
        </div>
      </div>

      {/* Period selector */}
      <div className="expenses-filters">
        <div className="filter-group">
          <label>Period</label>
          <select value={rangePreset} onChange={e => setRangePreset(e.target.value)}>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="last_3_months">Last 3 Months</option>
            <option value="year_to_date">Year to Date</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        {rangePreset === 'custom' && (
          <>
            <div className="filter-group">
              <label>Start</label>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} />
            </div>
            <div className="filter-group">
              <label>End</label>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
            </div>
          </>
        )}
        <div className="filter-group">
          <label>Upcoming Sales Horizon</label>
          <select value={upcomingHorizon} onChange={e => setUpcomingHorizon(e.target.value)}>
            <option value="7">Next 7 days</option>
            <option value="14">Next 14 days</option>
            <option value="30">Next 30 days</option>
            <option value="60">Next 60 days</option>
            <option value="90">Next 90 days</option>
          </select>
        </div>
        <div className="filter-group" style={{ alignSelf: 'center' }}>
          <label>&nbsp;</label>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            Showing: <strong style={{ color: 'var(--navy)' }}>{rangeLabel}</strong>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="expenses-summary">
        <div className="summary-card">
          <div className="card-icon"><TrendingUp size={22} /></div>
          <div className="card-content">
            <div className="card-value">{formatCurrency(totalIncome)}</div>
            <div className="card-label">Income (deposits/sales)</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon"><TrendingDown size={22} /></div>
          <div className="card-content">
            <div className="card-value">{formatCurrency(totalExpenses)}</div>
            <div className="card-label">Expenses</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon"><Users size={22} /></div>
          <div className="card-content">
            <div className="card-value">{formatCurrency(estimatedPayrollTotal)}</div>
            <div className="card-label">Est. Payroll ({estimatedPayrollHours.toFixed(1)} hrs)</div>
          </div>
        </div>
        <div className={`summary-card ${netIncome >= 0 ? 'highlight' : 'warning'}`}>
          <div className="card-icon"><Wallet size={22} /></div>
          <div className="card-content">
            <div className="card-value">{formatCurrency(netIncome)}</div>
            <div className="card-label">Net Income (P&amp;L)</div>
          </div>
        </div>
      </div>

      {/* Upcoming sales */}
      <div className="expenses-table-section" style={{ marginBottom: 24 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--cream-dark)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Calendar size={18} />
            <h3 style={{ margin: 0, fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
              Upcoming Sales
            </h3>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              unpaid invoices due in the next {upcomingHorizon} days
            </span>
          </div>
          <div style={{ fontWeight: 700, color: 'var(--navy)' }}>
            {formatCurrency(upcomingSalesTotal)}
          </div>
        </div>
        {upcomingSales.length === 0 ? (
          <div className="expenses-empty">
            <Calendar size={40} />
            <p>No upcoming invoices in this window</p>
          </div>
        ) : (
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Client</th>
                <th>Due Date</th>
                <th>Status</th>
                <th className="right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {upcomingSales.map(inv => (
                <tr key={inv.id}>
                  <td>
                    <Link to={`/admin/invoices/${inv.id}`}>#{inv.invoiceNumber}</Link>
                  </td>
                  <td>{inv.clientName}</td>
                  <td>{inv.dueDate}</td>
                  <td><span className="account-badge">{inv.status}</span></td>
                  <td className="right amount income">{formatCurrency(inv.balance)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4"><strong>Total</strong></td>
                <td className="right amount income">{formatCurrency(upcomingSalesTotal)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Estimated payroll */}
      <div className="expenses-table-section" style={{ marginBottom: 24 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--cream-dark)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Users size={18} />
            <h3 style={{ margin: 0, fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
              Estimated Payroll
            </h3>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              scheduled shifts in period × each employee's hourly rate
            </span>
          </div>
          <div style={{ fontWeight: 700, color: 'var(--navy)' }}>
            {formatCurrency(estimatedPayrollTotal)}
          </div>
        </div>
        {payrollByEmployee.length === 0 ? (
          <div className="expenses-empty">
            <Users size={40} />
            <p>No scheduled shifts in this period</p>
          </div>
        ) : (
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th className="right">Hours</th>
                <th className="right">Hourly Rate</th>
                <th className="right">Estimated Cost</th>
              </tr>
            </thead>
            <tbody>
              {payrollByEmployee.map(p => (
                <tr key={p.name}>
                  <td>{p.name}</td>
                  <td className="right">{p.hours.toFixed(2)}</td>
                  <td className="right">{formatCurrency(p.rate)}</td>
                  <td className="right amount expense">{formatCurrency(p.cost)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                <td className="right"><strong>{estimatedPayrollHours.toFixed(2)}</strong></td>
                <td />
                <td className="right amount expense">{formatCurrency(estimatedPayrollTotal)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Categorized expenses (P&L rollup) */}
      <div className="expenses-table-section" style={{ marginBottom: 24 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--cream-dark)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PieChart size={18} />
            <h3 style={{ margin: 0, fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
              Expenses by Category
            </h3>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              rolled up to parent category
            </span>
          </div>
          <div style={{ fontWeight: 700, color: 'var(--navy)' }}>
            {formatCurrency(totalExpenses)}
          </div>
        </div>
        {expenseBreakdown.length === 0 ? (
          <div className="expenses-empty">
            <PieChart size={40} />
            <p>No expenses in this period</p>
          </div>
        ) : (
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Category</th>
                <th className="right">Amount</th>
                <th className="right">% of Expenses</th>
              </tr>
            </thead>
            <tbody>
              {expenseBreakdown.map(parent => (
                <React.Fragment key={parent.name}>
                  <tr>
                    <td><strong>{parent.name}</strong></td>
                    <td className="right amount expense"><strong>{formatCurrency(parent.total)}</strong></td>
                    <td className="right">
                      {totalExpenses > 0 ? `${((parent.total / totalExpenses) * 100).toFixed(1)}%` : '0.0%'}
                    </td>
                  </tr>
                  {parent.children.map(child => (
                    <tr key={`${parent.name}-${child.name}`}>
                      <td style={{ paddingLeft: 40 }}>
                        <span style={{ color: 'var(--text-muted)', marginRight: 8 }}>↳</span>
                        {child.name}
                      </td>
                      <td className="right">{formatCurrency(child.total)}</td>
                      <td className="right" style={{ color: 'var(--text-muted)' }}>
                        {totalExpenses > 0 ? `${((child.total / totalExpenses) * 100).toFixed(1)}%` : '0.0%'}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                <td className="right amount expense"><strong>{formatCurrency(totalExpenses)}</strong></td>
                <td className="right">100%</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* P&L summary */}
      <div className="expenses-table-section">
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--cream-dark)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <DollarSign size={18} />
          <h3 style={{ margin: 0, fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
            Profit &amp; Loss Summary
          </h3>
        </div>
        <table className="expenses-table">
          <tbody>
            <tr>
              <td>Total Income</td>
              <td className="right amount income">{formatCurrency(totalIncome)}</td>
            </tr>
            <tr>
              <td>Total Expenses (recorded)</td>
              <td className="right amount expense">({formatCurrency(totalExpenses)})</td>
            </tr>
            <tr>
              <td style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Estimated payroll (informational — included in expenses if tracked there)
              </td>
              <td className="right" style={{ color: 'var(--text-muted)' }}>
                {formatCurrency(estimatedPayrollTotal)}
              </td>
            </tr>
            <tr style={{ background: 'var(--cream)' }}>
              <td><strong>Net Income</strong></td>
              <td className={`right amount ${netIncome >= 0 ? 'income' : 'expense'}`}>
                <strong>{formatCurrency(netIncome)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
        <div style={{ padding: '12px 20px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Estimated payroll is a forward-looking cost based on scheduled shifts × hourly rates. Record actual payroll under Expenses (category: Payroll) to include it in the P&amp;L above.
        </div>
      </div>
    </div>
  );
}

export default CashflowReport;
